/**
 * Display spec for a linear meter: always laid out left → right as
 * 0 → far bound, regardless of whether the metric's values are positive
 * (Electrical Power, Compressed Air Pressure, …) or negative (Vacuum
 * Pressure). This keeps every meter — whichever sign its metric happens to
 * read — aligned the same way in a row of cards, with the fill always
 * growing rightward from the shared 0 baseline.
 *
 * When both bounds are known (hintMin AND hintMax given), that's a fixed
 * scale: every gauge for that metric uses the exact same far endpoint, so a
 * row of cards lines up and stays stable across refreshes instead of
 * rescaling per reading. It still never clamps — a live value past the bound
 * is abnormal, not invalid, so the far endpoint extends just enough to show it.
 *
 * When only one (or neither) bound is known, the far endpoint is dynamic: it
 * grows to fit the live value and recent history.
 */
export interface GaugeDisplay {
  /** Left-edge label — always "0", the shared baseline. */
  leftLabel: string;
  /** Right-edge label — the far bound (may be negative, e.g. vacuum). */
  rightLabel: string;
  /** 0–100, how far the fill extends from the left (0) edge. */
  fillPct: number;
}

export function computeGaugeDisplay(
  value: number,
  hintMin?: number,
  hintMax?: number,
  points?: { t: number; v: number }[],
): GaugeDisplay {
  let far: number;
  if (hintMin !== undefined && hintMax !== undefined) {
    // Fixed scale — pick whichever configured bound is the non-zero side,
    // extending it if the live value overshoots.
    const bound = hintMin !== 0 ? hintMin : hintMax;
    far = bound < 0 ? Math.min(bound, value) : Math.max(bound, value);
  } else {
    const candidates = [hintMin, hintMax, value, ...(points?.map((p) => p.v) ?? [])].filter(
      (n): n is number => n !== undefined,
    );
    const lo = Math.min(...candidates, 0);
    const hi = Math.max(...candidates, 0);
    // Whichever side of 0 has the larger magnitude is the far endpoint.
    far = Math.abs(lo) > Math.abs(hi) ? lo : hi;
    if (far === 0) far = value < 0 ? -1 : 1; // no spread yet — placeholder scale
  }

  const fillPct = Math.min(Math.max((value / far) * 100, 0), 100);
  return {
    leftLabel: formatRangeLabel(0),
    rightLabel: formatRangeLabel(far),
    fillPct,
  };
}

/** Compact label for a dynamic range endpoint: whole numbers stay whole. */
export function formatRangeLabel(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
